import { splitEncoding } from '@contentful/live-preview';
import { useContext, useEffect, useRef, useState } from 'react';

import { ProductTypeEnum } from '@/types/product';
import { getLinkAriaLabel } from '@/utils/urlUtils';
import useMemoizedFn from '@/utils/useMemoizedFn';

import { ProductCard } from '@/cms/ProductCard';
import { Container } from '@/components/Container';
import { Flex } from '@/components/Flex';
import { ProductSlider } from '@/components/ProductSlider';
import { SliderOverlay } from '@/components/SliderOverlay';
import type {
  ContentfulHeadingFragment,
  ContentfulSliderProductsManualFragment,
} from '@/graphql/contentful/api';
import type { ProductCardFragmentWithVariantCta } from '@/hooks/useFetchProductsWithCta';
import { useFetchProductsWithCta } from '@/hooks/useFetchProductsWithCta';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsMdDown } from '@/hooks/useMedia';
import { useSendViewedAlgoliaEvent } from '@/hooks/useSendAlgoliaEvent';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { filterHits, getIndexName } from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type {
  AlgoliaRecommendation,
  WatchHit,
} from '@/integrations/algolia/types';
import type { ProductData } from '@/integrations/contentful';
import { EVENT_NAME } from '@/lib/gtm/const';
import { getProductPath, getWatchCollectionPath } from '@/lib/product';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { theme } from '@/theme';

import { Cta } from '../Card/components/CTA';
import { Heading } from '../Heading';
import * as S from './SliderProducts.style';

type SliderProductsProps = Omit<
  ContentfulSliderProductsManualFragment,
  'sys' | 'contentfulMetadata' | '__typename' | 'linkedFrom'
> & {
  /** Products of the slider that Algolia Recommend returned, with its queryID */
  algoliaRecommendation?: AlgoliaRecommendation;
  autoLoadData?: boolean;
  disableGutters?: boolean;
  draftMode?: boolean;
  /** Holds the Algolia view while the products shown are not the final list */
  isAlgoliaViewPending?: boolean;
  isAutomaticSlider?: boolean;
  orderedSlugs?: string[];
  productData?: ProductData[];
  title?: string | null;
};

const ALGOLIA_VIEW_SETTLE_MS = 1000;

export enum SliderProductsTypeEnum {
  CompatibleStraps = 'compatible straps',
  YouMayAlsoLike = 'you may also like',
  RecentlyViewed = 'recently viewed',
  Default = 'Default',
  TrendingNow = 'Trending now',
  DiscoverTheCollection = 'Discover the collection',
}

export const SliderProducts = ({
  algoliaRecommendation,
  autoLoadData = false,
  backgroundColor,
  backgroundImage,
  ctaStyle,
  ctaText,
  ctaUrl,
  disableGutters,
  draftMode,
  headingEntry,
  isAlgoliaViewPending = false,
  isAutomaticSlider,
  orderedSlugs,
  productCardBackgroundColor,
  productData = [],
  products,
  showCta,
  showLabel,
  showName = true,
  showPrice,
  showWishlist,
  slidesPerView,
  slidesPerViewForMobile,
  title,
  type = 'Default',
}: SliderProductsProps) => {
  const scrollbar = true;
  const spaceBetween = 15;
  const bgHeight = 144;
  const buttonHeight = 40;
  const topOffset = 0;
  const [sliderNode, setSliderNode] = useState<HTMLElement | null>(null);
  const [sliderWidth, setSliderWidth] = useState<number>(0);
  const resize = useWindowWidth();
  const isMobileView = useIsMdDown();
  const slidesPerViewNum = slidesPerView ?? 4;
  const slidesPerViewForMobileNum = slidesPerViewForMobile ?? 2.45;
  const slideRelaviteSizes = `(max-width: 991px) ${Math.round(
    100 / slidesPerViewForMobileNum,
  )}vw, ${Math.round(100 / slidesPerViewNum)}vw`;
  const slidesNum = isMobileView ? slidesPerViewForMobileNum : slidesPerViewNum;
  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
  });
  const [, setActiveIndex] = useState<number>(0);

  const { sendEvent, sendViewItemListEvent } = useGoogleAnalytics();
  const sendViewedAlgoliaEvent = useSendViewedAlgoliaEvent();
  const sentViewKeys = useRef(new Set<string>());
  const {
    locale: { code: languageCode },
    productCatalogMode,
  } = useRegionProvider();

  useEffect(() => {
    if (sliderNode) {
      setSliderWidth(sliderNode.offsetWidth);
    }
    // BEC-24811: keyed on the node as well as the resize. An automatic slider
    // renders null until Algolia answers, so the container mounts after the
    // first commit and a resize-only dependency never measured it.
  }, [sliderNode, resize]);

  const productsSkus: string[] = (products ?? [])
    .map((s) => splitEncoding(s ?? '').cleaned)
    .filter((item): item is string => !!item);

  const { products: productsWithVariantCta } = useFetchProductsWithCta({
    filter: { slugs: productsSkus },
    orderedSlugs: orderedSlugs,
    excludeSlug: undefined,
    existProductsData: productData,
    skip: !autoLoadData,
    skipFetchCta: productCatalogMode,
  });

  const [productsFromAlgolia, setProductsFromAlgolia] = useState<WatchHit[]>(
    [],
  );
  const locale = useContext(LocaleContext);

  const skusForAlgolia = productsWithVariantCta
    .filter(
      (product) =>
        //BEC-7313 Filter products based on results and configuration filters
        isAutomaticSlider ||
        //BEC-17966, eyewear do not have launchDate in slaeor ,fetch it from algolia
        (showLabel && product.productType.slug === ProductTypeEnum.Eyewear),
    )
    .map((product) => product.defaultVariant?.sku)
    .filter(Boolean) as string[];

  const hasSsrLaunchDates =
    !isAutomaticSlider &&
    skusForAlgolia.every((sku) => {
      const product = productsWithVariantCta.find(
        (item) => item.defaultVariant?.sku === sku,
      );
      return typeof product?.launch_date === 'number';
    });

  const skusForAlgoliaKey = skusForAlgolia.join(',');
  const [checkedSkusKey, setCheckedSkusKey] = useState<string>();
  const latestSkusForAlgoliaKey = useRef<string>();
  const isAlgoliaCheckPending =
    skusForAlgolia.length > 0 &&
    !hasSsrLaunchDates &&
    checkedSkusKey !== skusForAlgoliaKey;

  const fetchFromAlgolia = useMemoizedFn(async () => {
    if (skusForAlgolia.length === 0 || hasSsrLaunchDates) return;
    const indexName = getIndexName('PRODUCT', locale);
    const objectIDs = skusForAlgolia.map((sku) => ({
      indexName,
      objectID: sku,
    }));
    const requestedKey = skusForAlgoliaKey;
    latestSkusForAlgoliaKey.current = requestedKey;
    try {
      const results = await algoliaSearchClient.multipleGetObjects(objectIDs);
      if (latestSkusForAlgoliaKey.current !== requestedKey) return;
      setProductsFromAlgolia(
        isAutomaticSlider
          ? filterHits(results, !!draftMode)
          : ((results.results ?? []) as WatchHit[]),
      );
    } finally {
      if (latestSkusForAlgoliaKey.current === requestedKey) {
        setCheckedSkusKey(requestedKey);
      }
    }
  });

  useEffect(() => {
    fetchFromAlgolia();
  }, [skusForAlgolia.join('|'), draftMode, locale]);

  const isInited = sliderWidth !== 0;

  const bgOffset = isInited
    ? sliderWidth / slidesPerViewNum / 2 - bgHeight / 2 + topOffset
    : undefined;
  const buttonOffset = isInited
    ? sliderWidth / slidesPerViewNum / 2 - buttonHeight / 2 + topOffset
    : undefined;
  const minHeight = isInited
    ? `${sliderWidth / slidesPerViewNum}px`
    : undefined;

  const headingData: ContentfulHeadingFragment | undefined = headingEntry
    ? headingEntry
    : title
      ? ({
          text: title,
          headingSeoTag: 'h2',
          headingType: 'h2',
          mainColor: theme.colors.black,
          highlightColor: theme.colors.primary,
          textAlignment: 'left',
        } as ContentfulHeadingFragment)
      : undefined;

  const isDiscoverTheCollection =
    type === SliderProductsTypeEnum.DiscoverTheCollection;
  const isTrendingNow = type === SliderProductsTypeEnum.TrendingNow;

  const productsToShow = productsWithVariantCta.flatMap((product) => {
    const matchingHit = productsFromAlgolia.find(
      (hit: WatchHit) => hit.objectID === product?.defaultVariant?.sku,
    );

    if (matchingHit) {
      return [{ ...product, launch_date: matchingHit?.launch_date ?? 0 }];
    } else {
      return isAutomaticSlider ? [] : [product];
    }
  });

  // Keyed on the shown skus: Algolia narrowing the list must refire, prices and CTAs arriving must not.
  const shownSkusKey = productsToShow
    .map((product) => product.defaultVariant?.sku)
    .join('|');

  useEffect(() => {
    sendViewItemEvent(0, slidesNum);
  }, [shownSkusKey, slidesNum]);

  const sendViewItemEvent = (start: number, end: number) => {
    const skus = productsToShow
      .map((product) => product.defaultVariant?.sku as string)
      .slice(start, end);

    sendViewItemListEvent({
      skus: skus,
      itemListName: headingData?.text?.replace(/[[\]]/g, ''),
    });
  };

  const onClickCta = () => {
    sendEvent({
      event: EVENT_NAME.CTA_DISCOVER,
      cta_title: headingData?.text?.replace(/[[\]]/g, ''),
      click_cta_name: ctaText as string,
    });
  };

  const onSlideChange = (activeIndex: number) => {
    setActiveIndex((prev) => {
      if (prev >= activeIndex) return prev;

      sendViewItemEvent(slidesNum + prev, slidesNum + activeIndex);
      return activeIndex;
    });
  };

  const sortedProductsWithVariantCta =
    productsSkus.length !== 0
      ? productsSkus
          .map((sku) => {
            return productsToShow.find(
              (product) => product.defaultVariant?.sku === sku,
            );
          })
          .filter((item): item is ProductCardFragmentWithVariantCta => !!item)
      : productsToShow;

  const shownSkus = isDiscoverTheCollection
    ? []
    : sortedProductsWithVariantCta
        .map((product) => product.defaultVariant?.sku)
        .filter((sku): sku is string => !!sku);

  const algoliaViewKey = [...shownSkus]
    .sort((a, b) => a.localeCompare(b))
    .join(',');
  useEffect(() => {
    if (isAlgoliaViewPending || isAlgoliaCheckPending || !algoliaViewKey)
      return;
    if (sentViewKeys.current.has(algoliaViewKey)) return;
    // The list changes several times while its products, CTAs and
    // recommendations load (and keeps the previous page's products for a
    // moment after a client-side navigation); only the settled list is a view.
    const timeoutId = setTimeout(() => {
      sentViewKeys.current.add(algoliaViewKey);
      sendViewedAlgoliaEvent(shownSkus);
    }, ALGOLIA_VIEW_SETTLE_MS);

    return () => clearTimeout(timeoutId);
  }, [algoliaViewKey, isAlgoliaViewPending, isAlgoliaCheckPending]);

  const getAlgoliaListPosition = (
    sku: string | null | undefined,
    index: number,
  ) => {
    if (!sku || !algoliaRecommendation?.objectIDs.includes(sku)) {
      return undefined;
    }
    return { queryID: algoliaRecommendation.queryID, position: index + 1 };
  };

  // if showName is null, the value is true
  const finalShowName = showName !== null ? showName : true;

  if (sortedProductsWithVariantCta.length === 0) return null;

  return (
    <Flex flexDirection='column' justifyContent='center' maxWidth='100%'>
      {headingData && (
        <S.HeadingWrapper
          data-test-id='SliderProductsHeadingWrapper'
          disableGutters={disableGutters}
          maxWidth='lg'
        >
          <Heading Heading={headingData} data-test-id='SliderProductsHeading' />
        </S.HeadingWrapper>
      )}
      <SliderOverlay
        backgroundColor={backgroundColor?.value}
        backgroundOffset={bgOffset}
        backgroundSrc={backgroundImage?.url}
        data-test-id='SliderProductsSliderOverlay'
      >
        <Container
          ref={setSliderNode}
          data-test-id='SliderProductsContainer'
          disableGutters={disableGutters}
          maxWidth='lg'
        >
          <ProductSlider
            buttonOffset={buttonOffset}
            data-test-id='SliderProductsProductSlider'
            minHeight={minHeight}
            variantColor='dark'
          >
            <ProductSlider.Slider
              data-test-id='SliderProductsSlider'
              hideNavigation={!isInited}
              onSlideChange={onSlideChange}
              scrollbar={scrollbar}
              separator={false}
              slidesPerView={slidesPerView}
              slidesPerViewForMobile={slidesPerViewForMobileNum}
              spaceBetween={spaceBetween}
            >
              {sortedProductsWithVariantCta.map((product, index) => {
                return (
                  <S.ProductCardWrapper
                    key={product.slug}
                    data-test-id={`SliderProductsProductCard-${index}`}
                  >
                    <ProductCard
                      key={product.slug}
                      algoliaListPosition={getAlgoliaListPosition(
                        product.defaultVariant?.sku,
                        index,
                      )}
                      backgroundColor={productCardBackgroundColor || undefined}
                      href={
                        isDiscoverTheCollection
                          ? getWatchCollectionPath(product)
                          : getProductPath(product)
                      }
                      isLinkToCollection={isDiscoverTheCollection}
                      productData={product}
                      showCollection={finalShowName && isDiscoverTheCollection}
                      showCta={showCta}
                      showLabel={showLabel}
                      showName={finalShowName && !isDiscoverTheCollection}
                      showPrice={!isDiscoverTheCollection && showPrice}
                      showSku={!isDiscoverTheCollection}
                      showWishlist={showWishlist}
                      sizes={slideRelaviteSizes}
                      type={isTrendingNow ? 'Watch-WristOnHover' : 'Default'}
                    />
                  </S.ProductCardWrapper>
                );
              })}
            </ProductSlider.Slider>
          </ProductSlider>
        </Container>
      </SliderOverlay>
      {ctaStyle && ctaStyle !== 'none' && ctaText ? (
        <S.ButtonWrapper data-test-id='SliderProductsButtonWrapper'>
          <Cta
            ariaLabel={getLinkAriaLabel(ctaUrl ?? '', languageCode)}
            onClickCta={onClickCta}
            url={ctaUrl}
            urlText={ctaText}
            urlType={ctaStyle}
          />
        </S.ButtonWrapper>
      ) : null}
    </Flex>
  );
};
